import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { BillParams } from '../domain/bill/bill-params';
import { DietitianId } from '../domain/dietitian';
import { Picture } from '../domain/picture/picture';
import { PictureRepository } from './picture_repository';

export interface BillParamsSchema {
  logoUrl?: string | null;
  logoPath?: string | null;
  logoPosition?: 'left' | 'center' | 'right' | null;
  logoSize?: 'small' | 'medium' | 'large' | null;
  signUrl?: string | null;
  signPath?: string | null;
  signSize?: 'small' | 'medium' | 'large' | null;
  socialReason?: string | null;
  siren?: string | null;
  registrationNumber?: string | null;
  address?: string | null;
  zip?: string | null;
  city?: string | null;
  country?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  tax: 'with' | 'without' | 'both';
  taxNumber?: string | null;
  taxRate?: number | null;
  noTaxMention: boolean;
  footer?: string | null;
  previewUrl?: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  rcs: boolean | null;
  late: boolean | null;
  aga: boolean | null;
  identifier?: string | null;
  identifierId?: number | null;
  identifierStatus?: string | null;
  identifierStatusMessage?: string | null;
  otherIdentifiers?: { identifier: string; status: string }[] | null;
  delayedIdentifier?: boolean | null;
  delayedReason?: string | null;
}

@Injectable()
export class BillParamsRepository {
  constructor(
    private firestore: AngularFirestore,
    private pictureRepository: PictureRepository,
  ) {
    // Nada
  }

  private collection(dietitianId: string, queryFn?: QueryFn) {
    return this.firestore
      .collection('dietitians')
      .doc(dietitianId)
      .collection<BillParamsSchema>('bill_params', queryFn);
  }

  private toSchema(billParams: BillParams): BillParamsSchema {
    return {
      logoUrl: billParams.logo?.url ?? null,
      logoPath: billParams.logo?.path ?? null,
      logoPosition: billParams.logoPosition ?? null,
      logoSize: billParams.logoSize ?? null,
      signUrl: billParams.sign?.url ?? null,
      signPath: billParams.sign?.path ?? null,
      signSize: billParams.signSize ?? null,
      socialReason: billParams.socialReason ?? null,
      siren: billParams.siren ?? null,
      registrationNumber: billParams.registrationNumber ?? null,
      address: billParams.address ?? null,
      zip: billParams.zip ?? null,
      city: billParams.city ?? null,
      country: billParams.country ?? null,
      phoneNumber: billParams.phoneNumber ?? null,
      email: billParams.email ?? null,
      tax: billParams.tax,
      taxNumber: billParams.taxNumber ?? null,
      taxRate: billParams.taxRate ?? null,
      noTaxMention: billParams.noTaxMention,
      footer: billParams.footer ?? null,
      previewUrl: billParams.previewUrl ?? null,
      createdAt: billParams.createdAt
        ? Timestamp.fromDate(billParams.createdAt)
        : Timestamp.now(),
      updatedAt: Timestamp.now(),
      rcs: billParams.rcs ?? false,
      late: billParams.late ?? false,
      aga: billParams.aga ?? false,
      identifier: billParams.identifier ?? null,
      identifierId: billParams.identifierId ?? null,
      identifierStatus: billParams.identifierStatus ?? null,
      identifierStatusMessage: billParams.identifierStatusMessage ?? null,
      otherIdentifiers: billParams.otherIdentifiers ?? null,
      delayedIdentifier: billParams.delayedIdentifier ?? false,
      delayedReason: billParams.delayedReason ?? null,
    };
  }

  private fromSchema(
    schema: BillParamsSchema,
    dietitianId: string,
    id?: string,
  ): BillParams {
    return BillParams.create(
      {
        dietitianId: DietitianId.create(new UniqueEntityID(dietitianId)),
        logo:
          schema.logoUrl && schema.logoPath
            ? Picture.create({
                url: schema.logoUrl,
                path: schema.logoPath,
              })
            : undefined,
        logoPosition: schema.logoPosition ?? undefined,
        logoSize: schema.logoSize ?? undefined,
        sign:
          schema.signUrl && schema.signPath
            ? Picture.create({
                url: schema.signUrl,
                path: schema.signPath,
              })
            : undefined,
        signSize: schema.signSize ?? undefined,
        socialReason: schema.socialReason ?? undefined,
        siren: schema.siren ?? undefined,
        registrationNumber: schema.registrationNumber ?? undefined,
        address: schema.address ?? undefined,
        zip: schema.zip ?? undefined,
        city: schema.city ?? undefined,
        country: schema.country ?? undefined,
        phoneNumber: schema.phoneNumber ?? undefined,
        email: schema.email ?? undefined,
        tax: schema.tax,
        taxNumber: schema.taxNumber ?? undefined,
        taxRate: schema.taxRate ?? undefined,
        noTaxMention: schema.noTaxMention,
        footer: schema.footer ?? undefined,
        previewUrl: schema.previewUrl ?? undefined,
        createdAt: schema.createdAt.toDate(),
        updatedAt: schema.updatedAt.toDate(),
        rcs: schema.rcs ?? false,
        late: schema.late ?? false,
        aga: schema.aga ?? false,
        identifier: schema.identifier ?? undefined,
        identifierId: schema.identifierId ?? undefined,
        identifierStatus: schema.identifierStatus ?? undefined,
        identifierStatusMessage: schema.identifierStatusMessage ?? undefined,
        otherIdentifiers: schema.otherIdentifiers ?? undefined,
        delayedIdentifier: schema.delayedIdentifier ?? false,
        delayedReason: schema.delayedReason ?? undefined,
      },
      new UniqueEntityID(id),
    );
  }

  async save(billParams: BillParams, create = false): Promise<BillParams> {
    const dietitianId = billParams.dietitianId.id.toString();
    const schema = this.toSchema(billParams);

    if (create) {
      await this.collection(dietitianId).doc(dietitianId).set(schema);
      return this.fromSchema(schema, dietitianId, dietitianId);
    } else {
      const oldBillParams = await this.load(
        billParams.dietitianId.id.toString(),
      );

      if (billParams.logo) {
        if (
          !oldBillParams ||
          !oldBillParams.logo ||
          !oldBillParams.logo.equals(billParams.logo)
        ) {
          if (oldBillParams && oldBillParams.logo) {
            await this.pictureRepository.delete(oldBillParams.logo);
          }
          const savedLogo = await this.pictureRepository.save(billParams.logo);
          schema.logoUrl = savedLogo.url;
          schema.logoPath = savedLogo.path;
          schema.logoPosition = schema.logoPosition
            ? schema.logoPosition
            : 'left';
          schema.logoSize = schema.logoSize ? schema.logoSize : 'medium';
        }
      }

      if (billParams.sign) {
        if (
          !oldBillParams ||
          !oldBillParams.sign ||
          !oldBillParams.sign.equals(billParams.sign)
        ) {
          if (oldBillParams && oldBillParams.sign) {
            await this.pictureRepository.delete(oldBillParams.sign);
          }
          const savedSign = await this.pictureRepository.save(billParams.sign);
          schema.signUrl = savedSign.url;
          schema.signPath = savedSign.path;
        }
      }

      await this.collection(dietitianId).doc(dietitianId).set(schema);
      return this.fromSchema(schema, dietitianId);
    }
  }

  async load(dietitianId: string): Promise<BillParams> {
    if (dietitianId) {
      const snap = await firstValueFrom(
        this.collection(dietitianId).doc(dietitianId).get(),
      );
      if (!snap.exists) {
        const newBillParams = BillParams.create({
          dietitianId: DietitianId.create(new UniqueEntityID(dietitianId)),
          tax: 'with',
          noTaxMention: false,
          rcs: false,
          late: false,
          aga: false,
        });
        return await this.save(newBillParams, true);
      } else {
        const schema = snap.data() as BillParamsSchema;
        return this.fromSchema(schema, dietitianId, snap.id);
      }
    } else {
      throw new Error(
        "Impossible de lire les paramètres d'un.e diététicien.ne inconnu.e !",
      );
    }
  }

  listenBillParams(dietitianId: string) {
    return this.collection(dietitianId).doc(dietitianId).valueChanges();
  }
}
